"use client";

import { useMediaQuery } from "@mui/material";
import { createContext, useCallback, useMemo, useState } from "react";

const FiltersContext = createContext();

const FiltersProvider = ({ children }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");

  const [filters, setFilters] = useState({
    category: "",
    genre: "",
    country: "",
    imdb: {
      imdbRateStartAt: "",
      imdbRateEndAt: "",
    },
    year: {
      yearStartAt: "",
      yearEndAt: "",
    },
    kind: "",
    language: "",
    contentAgeRange: "",
    sortingType: "",
  });

  const setCategory = useCallback((category) => {
    setFilters((current) =>
      current.category === category ? current : { ...current, category }
    );
  }, []);

  const setGenre = useCallback((genre) => {
    setFilters((current) =>
      current.genre === genre ? current : { ...current, genre }
    );
  }, []);

  const setCountry = useCallback((country) => {
    setFilters((current) =>
      current.country === country ? current : { ...current, country }
    );
  }, []);

  const setIMDB = useCallback((imdb) => {
    setFilters((current) => {
      const nextIMDB = {
        imdbRateStartAt: imdb?.[0] ?? "",
        imdbRateEndAt: imdb?.[1] ?? "",
      };

      if (
        current.imdb.imdbRateStartAt === nextIMDB.imdbRateStartAt &&
        current.imdb.imdbRateEndAt === nextIMDB.imdbRateEndAt
      ) {
        return current;
      }

      return { ...current, imdb: nextIMDB };
    });
  }, []);

  const setYear = useCallback((year) => {
    setFilters((current) => {
      const nextYear = {
        yearStartAt: year?.[0] ?? "",
        yearEndAt: year?.[1] ?? "",
      };

      if (
        current.year.yearStartAt === nextYear.yearStartAt &&
        current.year.yearEndAt === nextYear.yearEndAt
      ) {
        return current;
      }

      return { ...current, year: nextYear };
    });
  }, []);

  const setKind = useCallback((kind) => {
    setFilters((current) =>
      current.kind === kind ? current : { ...current, kind }
    );
  }, []);

  const setLanguage = useCallback((language) => {
    setFilters((current) =>
      current.language === language ? current : { ...current, language }
    );
  }, []);

  const setContentAgeRange = useCallback((contentAgeRange) => {
    setFilters((current) =>
      current.contentAgeRange === contentAgeRange
        ? current
        : { ...current, contentAgeRange }
    );
  }, []);

  const setSortingType = useCallback((sortingType) => {
    setFilters((current) =>
      current.sortingType === sortingType
        ? current
        : { ...current, sortingType }
    );
  }, []);

  const contextValue = useMemo(
    () => ({
      filters,
      setCategory,
      setGenre,
      setCountry,
      setIMDB,
      setYear,
      setKind,
      setLanguage,
      setContentAgeRange,
      setSortingType,
      isMobile,
    }),
    [
      filters,
      setCategory,
      setGenre,
      setCountry,
      setIMDB,
      setYear,
      setKind,
      setLanguage,
      setContentAgeRange,
      setSortingType,
      isMobile,
    ]
  );

  return (
    <FiltersContext.Provider value={contextValue}>
      {children}
    </FiltersContext.Provider>
  );
};

export { FiltersContext, FiltersProvider };
